/* eslint-disable jsx-a11y/interactive-supports-focus */
import React, {
  useContext, useEffect, useState, useRef, useCallback
} from 'react';
import classNames from 'classnames';
import { extend } from '@thd-nucleus/data-sources';
import {
  number, string, func, bool, shape, object, arrayOf
} from 'prop-types';
import { shipping } from '@thd-olt-component-react/delivery-options';
import { ExperienceContext, useStore, useConfigService } from '@thd-nucleus/experience-context';
import { CheckAvailability } from '@thd-olt-component-react/check-availability';
import { dataModel } from '../dataModel';
import { PickupHeader } from './PickupHeader';
import { DeliveryHeader } from './DeliveryHeader';
import { PickupTile } from './PickupTile';
import { DeliveryTile } from './DeliveryTile';
import { DualPath } from '../shared/DualPath/DualPath';
import { NotifyMe } from '../shared/NotifyMe/NotifyMe';
import { LiveGoods } from '../shared/LiveGoods/LiveGoods';
import {
  useFulfillmentGetData,
  useShippingGetData,
  useApplianceGetData,
} from '../../hooks/useFulfillmentTiles';
import { getFulfillmentInfoFromData } from '../../hooks/useFulfillmentGetTemplates';
import { useFulfillmentListeners } from '../../hooks/useFulfillmentListeners';
import { useHandleOnChange } from '../../hooks/useHandleOnChange';
import { Discontinued } from '../shared/Discontinued/Discontinued';
import { FulfillmentPlaceholder } from './Placeholder/FulfillmentPlaceholder';
import {
  getLiveGoodsStock,
  setDeliveryZip,
  getDeliveryZip,
  isNonMajorAppliance,
  getPickupStoreName,
  isMobile,
  getLocalStore,
  bopis,
  sth,
  bodfs,
  getExcludedStateName,
  isMajorApplianceProductType,
  isTwoTileQuantityUpdateCookie,
  isBackorderedSelection,
  isExchangeCustomer,
  isBopisNotFulfillableOOS
} from '../helper/utils';
import { isSthInStock } from '../../pod/helpers/pod-fulfillment-utils';
import './fulfillmentTiles.scss';
import { FULFILLMENT_METHODS } from '../constants';
import { CheckNearByStore } from './partials/store/CheckNearByStore';
import { DeliveryMessageUnderTile } from './DeliveryMessageUnderTile';
import { ExcludedShipState } from './ExcludedShipState/ExcludedShipState';
import { ApplianceAlerts } from './alerts/ApplianceAlerts';
import { NonMajorApplianceAlert } from './alerts/NonMajorApplianceAlert';

const FulfillmentTiles = (props) => {
  const {
    itemId,
    storeId,
    deliveryZip,
    quantity,
    covidAlert,
    dualPath,
    onChange,
    onDeliveryZipCodeChange,
    thresholds,
    currentSelectedFulfillment,
    directData,
    applianceDirectData,
    configurableApplianceItemIds,
    isBuyBoxLite,
    isCartTiles,
    localizerStoreDetails,
    quantityExceeded,
    addOnsAssemblyAndMore,
    cartAction,
    bypassQuantitySkip = false
  } = props;
  const { channel } = useContext(ExperienceContext);
  const isTwoTileQuantityUpdate = useConfigService('isTwoTileQuantityUpdate') || isTwoTileQuantityUpdateCookie();
  const { storeId: defaultStoreId, storeName: defaultStoreName, storeZip } = useStore();
  const storeName = getPickupStoreName(directData) || defaultStoreName || '';
  const [zipCode, setZipCode] = useState(deliveryZip || getDeliveryZip() || storeZip);
  const [selectedFulfillmentMethod, setSelectedFulfillmentMethod] = useState(currentSelectedFulfillment);
  const [showCheckAvailability, setShowCheckAvailability] = useState(false);
  const [ffmData, setFfmData] = useState({});
  const [secondaryFFM, setPreferSecondaryFFM] = useState({ isServiceOptedIn: false, prefer: false });
  const [applianceDeliveryData, setApplianceDeliveryData] = useState(null);
  const currentDeliveryFfm = useRef('');
  const numberOfAddOnsAndMore = addOnsAssemblyAndMore ? Object.keys(addOnsAssemblyAndMore).length : 0;
  const updateZipCode = (newZipCode) => {
    if (newZipCode) {
      setDeliveryZip(newZipCode);
      setZipCode(newZipCode);
    }
  };
  const [updatedQuantity, setQuantityToQuery] = useState(quantity);

  const onChangeOfZipCode = (data) => {
    const shippingModel = shipping(data?.shipping);
    updateZipCode(data?.shipping?.zipCode);
    // setShippingModel(updatedModel);
    if (shippingModel) {
      if (onDeliveryZipCodeChange) {
        onDeliveryZipCodeChange(isCartTiles ? data?.shipping?.zipCode
          : { ...shippingModel, zipCode: data?.shipping?.zipCode });
      }
    }
  };

  useEffect(() => {
    LIFE_CYCLE_EVENT_BUS.lifeCycle.on('NATIVE_APP.DELIVERY_ZIP', ({ output }) => {
      if (output?.deliveryZip) {
        setZipCode(output?.deliveryZip);
      }
    });
  }, []);

  useEffect(() => {
    // for cart
    if (isCartTiles) {
      setZipCode(deliveryZip);
    }

    // call to set quantity with a delay
    const timer = setTimeout(() => {
      // Update the quantity after a wait so as to avoid unnecessary re-renders.
      const { fulfillment = {} } = ffmData;
      const { fallbackMode = false } = fulfillment || {};
      if (!fallbackMode && isTwoTileQuantityUpdate && !quantityExceeded) {
        setQuantityToQuery(quantity);
      }
    }, 1000);
    return () => clearTimeout(timer);
  }, [deliveryZip, isCartTiles, quantity, ffmData.fulfillment]);

  const { data, error, loading } = useFulfillmentGetData({
    itemId,
    zipCode,
    storeId: storeId || defaultStoreId,
    directData,
    // send updated Quantity set after a delay to improve performance
    quantity: isTwoTileQuantityUpdate ? updatedQuantity : 1,
    bypassQuantitySkip
  });

  const isMajorAppliance = !!applianceDirectData || !!applianceDeliveryData
    || isMajorApplianceProductType(data?.product?.identifiers?.productType);

  const { shippingData, shippingLoading } = useShippingGetData({
    itemId,
    storeId: storeId || defaultStoreId,
    zipCode,
    quantity: isTwoTileQuantityUpdate ? updatedQuantity : quantity,
    value: data?.product?.pricing?.value,
    isSthAvailable: sth(data?.product?.fulfillment),
    directData,
    isMajorAppliance
  });

  useApplianceGetData({
    itemId,
    itemIds: configurableApplianceItemIds,
    zipCode,
    isMajorAppliance,
    applianceDirectData,
    setApplianceDeliveryData
  });

  useEffect(() => {
    if (data?.product) {
      setFfmData(getFulfillmentInfoFromData({
        data,
        itemId,
        covidAlert,
        quantity,
        zipCode,
        storeId: storeId || defaultStoreId,
        storeName,
        channel,
        thresholds,
        setShowCheckAvailability,
        currentDeliveryFfm: currentDeliveryFfm?.current,
        shippingData,
        isMajorAppliance,
        applianceDeliveryData,
        preferSecondaryFFM: secondaryFFM.prefer,
        isCartTiles,
        addOnsAssemblyAndMore
      }));
    }
  }, [data.product, quantity, shippingData, applianceDeliveryData, secondaryFFM, numberOfAddOnsAndMore]);

  const {
    fulfillment = {},
    info,
    identifiers,
    value,
    availabilityType,
    fulfillmentModels,
    optimalFulfillmentFromAPI,
  } = ffmData;

  const { fallbackMode = false } = fulfillment || {};
  const isBackorderedApplied = isBackorderedSelection(fulfillment?.backordered, selectedFulfillmentMethod);

  const getCurrentFulfillment = (selectedFulfillment, ffms) => {
    if (selectedFulfillment) {
      return ffms.find(
        (option) => option.method === selectedFulfillment
      );
    }
    return fulfillmentModels.find((option) => option.method === selectedFulfillment);
  };

  const isPickupFfmMethod = (currentFulfillmentMethod = '') => {
    return !!(currentFulfillmentMethod === FULFILLMENT_METHODS.BOPIS
      || currentFulfillmentMethod === FULFILLMENT_METHODS.BOSS
      || !currentFulfillmentMethod); // on page load variable will be empty
  };

  useEffect(() => {
    if (fulfillmentModels) {
      const currentFulfillment = getCurrentFulfillment(selectedFulfillmentMethod, fulfillmentModels);
      const currentFulfillmentMethod = currentFulfillment?.method || selectedFulfillmentMethod;
      const [storeObj = {}, shippingObj = {}] = fulfillmentModels;
      currentDeliveryFfm.current = shippingObj?.method;
      if (!currentFulfillment?.enabled) {
        if (info?.hasSubscription && shippingObj?.enabled && isSthInStock(data?.product)) {
          setSelectedFulfillmentMethod(shippingObj.method);
        } else if (storeObj?.enabled && isPickupFfmMethod(currentFulfillmentMethod)
          && !storeObj?.isPickupNearby) {
          setSelectedFulfillmentMethod(storeObj.method);
        } else if (shippingObj?.enabled) {
          setSelectedFulfillmentMethod(shippingObj.method);
        } else if (storeObj?.enabled && isPickupFfmMethod()) {
          // in case the delivery FFM (currentFFM) is not available, then default to the pickup
          setSelectedFulfillmentMethod(storeObj.method);
        }
      }
    }
  }, [fulfillmentModels, secondaryFFM]);

  useEffect(() => {
    // for open delivery zip overlay from cart
    if (window.LIFE_CYCLE_EVENT_BUS && isCartTiles) {
      window.LIFE_CYCLE_EVENT_BUS.off('cart.delivery-zip-modal');
      window.LIFE_CYCLE_EVENT_BUS.on('cart.delivery-zip-modal', () => {
        setShowCheckAvailability(true);
      });
    }
  }, [cartAction]);

  useHandleOnChange({
    onChange,
    getCurrentFulfillment,
    fulfillmentModels,
    availabilityType,
    selectedFulfillmentMethod,
    itemId,
    quantity,
    primaryStoreNum: applianceDeliveryData?.primaryStrNbr,
    isCartTiles,
    isBackorderedApplied
  });

  useFulfillmentListeners({
    fulfillmentModels,
    setSelectedFulfillmentMethod,
    setPreferSecondaryFFM,
    optimalFulfillmentFromAPI,
  });

  if (!fulfillmentModels || (isMajorAppliance && !applianceDeliveryData)) {
    return (
      <FulfillmentPlaceholder channel={channel} />
    );
  }

  const [storeObj = {}, shippingObj = {}] = fulfillmentModels;

  const checkAvailabilityComponent = (
    <span data-testid="check-availability">
      <CheckAvailability
        useDrawerModal
        showCheckAvailability={showCheckAvailability}
        setShowCheckAvailability={setShowCheckAvailability}
        itemId={itemId}
        price={parseInt((value * quantity).toFixed(2), 10)}
        zipCode={zipCode}
        onSubmitForm={(response) => {
          if (response) {
            onChangeOfZipCode(response);
          }
          setShowCheckAvailability(false);
        }}
        quantity={quantity}
      />
    </span>
  );

  if (storeObj.excluded && shippingObj.excluded) {
    return (
      <ExcludedShipState
        stateText={getExcludedStateName(fulfillment?.sthExcludedShipState)}
        itemId={itemId}
        checkAvailabilityComponent={checkAvailabilityComponent}
        showCheckAvailability={showCheckAvailability}
        setShowCheckAvailability={setShowCheckAvailability}
        addOnsAssemblyAndMore={addOnsAssemblyAndMore}
      />
    );
  }

  if (availabilityType?.discontinued) {
    return (
      <Discontinued twoTileToggle />
    );
  }

  if (getLiveGoodsStock(fulfillment) === 0) {
    return (
      <LiveGoods
        storeName={storeName}
        seasonStatusEligible={fulfillment?.seasonStatusEligible}
        twoTileToggle
      />
    );
  }

  if (dualPath) {
    return (
      <DualPath twoTileToggle />
    );
  }

  if ((storeObj.unavailable && shippingObj.unavailable)
    || (shippingObj.outOfStock && availabilityType?.type === 'Online')
    || (storeObj.outOfStock && availabilityType?.type === 'Shared' && shippingObj.outOfStock)) {
    return (
      <NotifyMe
        itemId={itemId}
        store={storeObj}
        shipping={shippingObj}
        twoTileToggle
      />
    );
  }

  const getWrapperClasses = (fulfillmentModel) => {
    return classNames({
      card: true,
      u__legal: isMobile(channel),
      'mobile--margin-right': true,
      'card-container': true,
      'u__text--primary': !fulfillmentModel.enabled,
      'card-selected': fulfillmentModel.method === selectedFulfillmentMethod && fulfillmentModel.enabled,
      'card-unselected': fulfillmentModel.method !== selectedFulfillmentMethod && fulfillmentModel.enabled,
      'card-enabled': fulfillmentModel.enabled,
      'card-unclickable': !fulfillmentModel.enabled,
      'card-info': fulfillmentModel.tooltip,
    });
  };

  return (
    <div className="fulfillment-tiles">
      <div className="fulfillment-tile-headers">
        { /* TODO: Determine best way to show either store from zipcode or nearby store */}
        <PickupHeader
          storeName={storeName}
          itemId={itemId}
          pickupUnavailable={
            (fulfillment?.fulfillmentOptions !== null && isBopisNotFulfillableOOS(fulfillment))
              ? false
              : (fulfillment?.fulfillmentOptions === null
            || getLocalStore(bopis(fulfillment))?.inventory?.isUnavailable
            || getLocalStore(bopis(fulfillment))?.inventory?.isOutOfStock)
          }
          isMajorAppliance={isMajorAppliance}
          isCartTiles={isCartTiles}
          localizerStoreName={localizerStoreDetails?.name}
          addOnsAssemblyAndMore={addOnsAssemblyAndMore}
          isExchangeCustomer={isExchangeCustomer()}
        />
        <DeliveryHeader
          zipCode={zipCode}
          showCheckAvailability={showCheckAvailability}
          setShowCheckAvailability={setShowCheckAvailability}
          isCartTiles={isCartTiles}
        />
      </div>
      <div className="fulfillment__change-zip-container" />
      {showCheckAvailability && (checkAvailabilityComponent)}
      <div className="fulfillment__content-wrapper card-deck">
        {storeObj.isPickupNearby ? (
          <CheckNearByStore
            itemId={itemId}
            addOnsAssemblyAndMore={addOnsAssemblyAndMore}
          >
            <PickupTile
              getWrapperClasses={getWrapperClasses}
              fulfillmentObj={storeObj}
              currentFulfillmentMethod={selectedFulfillmentMethod}
              setSelectedFulfillmentMethod={setSelectedFulfillmentMethod}
            />
          </CheckNearByStore>
        ) : (
          <PickupTile
            getWrapperClasses={getWrapperClasses}
            fulfillmentObj={storeObj}
            currentFulfillmentMethod={selectedFulfillmentMethod}
            setSelectedFulfillmentMethod={setSelectedFulfillmentMethod}
          />
        )}
        <DeliveryTile
          getWrapperClasses={getWrapperClasses}
          fulfillmentObj={shippingObj}
          currentFulfillmentMethod={selectedFulfillmentMethod}
          setSelectedFulfillmentMethod={setSelectedFulfillmentMethod}
        />
      </div>
      <div className={'fulfillment__fullcontent ' + (isCartTiles ? 'ffm_cart' : '')}>
        {getCurrentFulfillment(selectedFulfillmentMethod, fulfillmentModels)?.subTemplate
          && !getCurrentFulfillment(selectedFulfillmentMethod, fulfillmentModels)?.unavailable && (
            <>
              {getCurrentFulfillment(selectedFulfillmentMethod, fulfillmentModels).subTemplate}
              {isNonMajorAppliance(identifiers.productType, info) && <NonMajorApplianceAlert />}
            </>
          )}
        <ApplianceAlerts
          applianceDeliveryData={applianceDeliveryData}
          storeObj={storeObj}
        />
      </div>
      {!fallbackMode
        && (
          <DeliveryMessageUnderTile
            shippingData={shippingData}
            bodfsFulfillment={bodfs(fulfillment)}
            currDeliveryTileFfm={shippingObj?.method}
            isShippingLoading={shippingLoading}
            isMajorAppliance={isMajorAppliance}
            applianceDeliveryData={applianceDeliveryData}
            value={value}
            isBuyBoxLite={isBuyBoxLite}
            isCartTiles={isCartTiles}
          />
        )}
    </div>
  );
};

FulfillmentTiles.displayName = 'FulfillmentTiles';

FulfillmentTiles.propTypes = {
  /** An Item that has product highlights */
  itemId: string.isRequired,
  storeId: string,
  deliveryZip: string,
  covidAlert: bool,
  quantity: number,
  onChange: func,
  dualPath: bool,
  onDeliveryZipCodeChange: func,
  thresholds: shape({
    bodfsMinimumThreshold: number,
    bopisMinimumThreshold: number,
    bopisMinimumThresholdStores: string,
    bopisHolidayTiming: number
  }),
  currentSelectedFulfillment: string,
  // eslint-disable-next-line react/forbid-prop-types
  directData: object,
  // eslint-disable-next-line react/forbid-prop-types
  applianceDirectData: object,
  configurableApplianceItemIds: arrayOf(string),
  isBuyBoxLite: bool,
  isCartTiles: bool,
  // eslint-disable-next-line react/forbid-prop-types
  localizerStoreDetails: object,
  quantityExceeded: bool,
  // eslint-disable-next-line react/forbid-prop-types
  addOnsAssemblyAndMore: object,
  cartAction: string,
  bypassQuantitySkip: bool,
};

FulfillmentTiles.defaultProps = {
  quantity: 1,
  storeId: null,
  deliveryZip: '',
  covidAlert: false,
  onChange: () => { },
  onDeliveryZipCodeChange: () => { },
  dualPath: false,
  thresholds: {},
  currentSelectedFulfillment: '',
  directData: null,
  applianceDirectData: null,
  configurableApplianceItemIds: null,
  isBuyBoxLite: false,
  isCartTiles: false,
  localizerStoreDetails: {},
  quantityExceeded: false,
  addOnsAssemblyAndMore: null,
  cartAction: '',
  bypassQuantitySkip: false,
};

FulfillmentTiles.dataModel = extend(
  CheckAvailability,
  dataModel
);

export { FulfillmentTiles };
